import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  computed,
  model,
  signal,
  ViewChild,
  inject,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { BookManagementService } from '../../book-management.service';

import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-simple-chip-filter',
  templateUrl: './simple-chip-filter.component.html',
  styleUrls: ['./simple-chip-filter.component.scss'],
  imports: [
    MatSelectModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    FormsModule,
    MatTooltipModule,
  ],
})
export class SimpleChipFilterComponent implements OnInit, OnDestroy {
  private bmSvc = inject(BookManagementService);

  @ViewChild(MatAutocompleteTrigger) chipFilterAuto: MatAutocompleteTrigger;
  @Input() label;
  @Input() fieldVar;
  @Input() value;
  @Output() toggleFiltersUpdated = new EventEmitter();
  unsubscribe: Subject<any> = new Subject();
  chips = signal([]);
  separatorKeysCodes: number[] = [ENTER, COMMA];
  currentChip = model('');
  chipOptions = signal([]);
  filteredChips = computed(() => {
    const currentChip = this.currentChip().toLowerCase();
    return currentChip
      ? this.chipOptions().filter(chip =>
          chip.toLowerCase().includes(currentChip)
        )
      : this.chipOptions().slice();
  });

  ngOnInit(): void {
    // Seed the displayed chips from a pre-populated shared value (hydrated defaults).
    if (this.value && this.value().length > 0) {
      this.chips.set([...this.value()]);
    }

    this.bmSvc.updateBmOptions$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(filterVals => {
        if (filterVals[this.fieldVar]) {
          this.chipOptions.set(filterVals[this.fieldVar]);
        }
      });

    this.bmSvc.clearBmFilters$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.chips.set([]);
        this.value.update(() => []);
      });

    this.bmSvc.updateBmChips$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        if (this.value() && this.value().length > 0) {
          this.chips.set([...this.value()]);
        } else if (this.value() && this.value().length === 0) {
          this.chips.set([]);
          this.value.update(() => []);
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  clearChipSearch() {
    this.currentChip.set('');
  }

  removeChip(chipOption: string): void {
    this.chips.update(chips => {
      const index = chips.indexOf(chipOption);
      if (index < 0) {
        return chips;
      }

      chips.splice(index, 1);
      return [...chips];
    });
    this.value.update(chips => {
      const index = chips.indexOf(chipOption);
      if (index < 0) {
        return chips;
      }

      chips.splice(index, 1);
      return [...chips];
    });
    this.toggleFiltersUpdated.emit(true);
  }

  chipSelected(event: MatAutocompleteSelectedEvent): void {
    if (!this.chips().includes(event.option.value)) {
      this.chips.update(chips => [...chips, event.option.value]);
      this.value.update(chips => [...chips, event.option.value]);
    }
    this.currentChip.set('');
    event.option.deselect();
    this.toggleFiltersUpdated.emit(true);
  }

  chipDisplay(val) {
    const dispChip = this.chipOptions().find(opt => opt.NPN === val);
    if (dispChip) {
      return `${dispChip.Name} - ${dispChip.NPN}`;
    }

    return val;
  }

  onBlur() {
    setTimeout(() => {
      this.chipFilterAuto.autocomplete.showPanel = false;
    }, 250);
  }
}
